import { z } from 'zod';
import { adapterClient, handleApiError } from './client';
import {
  projectCommandSchema,
  validateProjectCommand,
  type ProjectCommand,
} from '@/types/project-command';

export const operationRefSchema = z.object({
  operationId: z.string().regex(/^0x[0-9a-f]{32}$/),
  status: z.enum([
    'AwaitingSignature',
    'ReadyToSubmit',
    'Submitted',
    'OutcomeUnknown',
    'Finalized',
    'Rejected',
    'Expired',
  ]),
});
export async function submitProjectCommand(
  input: ProjectCommand,
  key: string,
  signal?: AbortSignal
) {
  const command = projectCommandSchema.parse(input);
  z.string()
    .regex(/^0x[0-9a-f]{32}$/)
    .parse(key);
  validateProjectCommand(command);
  let path =
    command.kind === 'create'
      ? '/projects'
      : 'projectId' in command
        ? '/projects/' +
          command.projectId +
          (command.kind === 'cancel' ? '/cancel' : '/planning/' + command.kind)
        : '';
  let method: 'post' | 'put' | 'delete' | 'patch' = 'post';
  if (command.kind === 'project-evaluate')
    path = '/projects/' + command.projectId + '/evaluations';
  if (command.kind === 'skill-request') path = '/catalog/skill-requests';
  if ('projectId' in command && command.kind.startsWith('proposal-')) {
    path = '/projects/' + command.projectId + '/proposals';
    if ('proposalId' in command) path += '/' + command.proposalId;
    if (command.kind === 'proposal-update') method = 'put';
    if (command.kind === 'proposal-delete') method = 'delete';
    if (command.kind === 'proposal-submit') path += '/submit';
    if (command.kind === 'proposal-approve') path += '/approve';
    if (command.kind === 'proposal-changes') path += '/changes';
    if (command.kind === 'proposal-withdraw') path += '/withdraw';
    if (command.kind === 'proposal-delivery') {
      path += '/delivery';
      method = 'put';
    }
  }
  if (
    command.kind === 'task-delete' ||
    command.kind === 'task-create' ||
    command.kind === 'task-update' ||
    command.kind === 'task-progress'
  ) {
    path =
      '/projects/' +
      command.projectId +
      '/task-storages/' +
      command.storageId +
      '/tasks';
    if (command.kind === 'task-delete') {
      path += '/' + command.taskId;
      method = 'delete';
    }
    if (command.kind === 'task-update') {
      path += '/' + command.taskId;
      method = 'put';
    }
    if (command.kind === 'task-progress') {
      path += '/' + command.taskId + '/progress';
      method = 'patch';
    }
  }
  if (
    command.kind === 'completion-submit' ||
    command.kind === 'completion-accept'
  )
    path =
      '/projects/' +
      command.projectId +
      '/milestones/' +
      command.milestoneId +
      (command.kind === 'completion-submit'
        ? '/completion-submissions'
        : '/accept-completion');
  if (command.kind === 'completion-reject')
    path = '/completion-submissions/' + command.submissionId + '/rejection';
  if (command.kind === 'dispute-open') path = '/disputes';
  if (command.kind === 'planning-dispute')
    path = '/projects/' + command.projectId + '/planning/dispute';
  if (command.kind === 'dispute-response')
    path = '/disputes/' + command.disputeId + '/response';
  if (command.kind === 'bramp-deposit') path = '/bramp/deposits';
  if (command.kind === 'bramp-withdrawal') path = '/bramp/withdrawals';
  if (command.kind === 'bramp-confirm')
    path = '/admin/bramp/deposits/' + command.requestId + '/confirm';
  if (command.kind === 'bramp-cancel')
    path = '/bramp/withdrawals/' + command.requestId + '/cancel';
  try {
    const { data } = await adapterClient.request<unknown>({
      url: path,
      method,
      data:
        command.kind === 'dispute-open'
          ? { projectId: command.projectId, ...command.payload }
          : 'payload' in command
            ? command.payload
            : undefined,
      headers: { 'Idempotency-Key': key },
      signal,
    });
    const result = operationRefSchema.safeParse(data);
    if (!result.success)
      throw new Error(
        'The server returned an invalid operation reference. Retry the saved request.'
      );
    return result.data;
  } catch (error) {
    handleApiError(error, 'project-' + command.kind);
  }
}
